import { useState, type CSSProperties, type ReactNode } from 'react';
import { cx } from '@/lib/cx';
import type { GeoJSONPoint } from '@/lib/api/types';
import { fromCoordinates } from '@/lib/geo/geoPoint';
import { esriTileUrl, tileContaining } from '@/lib/geo/slippyMap';
import styles from './CourseTexture.module.css';

/**
 * Zoom for a texture tile. Low enough that one 256px tile covers the whole property,
 * which is all we need — this is a background, not a map. The real multi-tile loader
 * lives in the hole viewer.
 */
const TEXTURE_ZOOM = 15;
const THUMB_ZOOM = 16;

function tileUrlFor(location: GeoJSONPoint | null | undefined, zoom: number): string | null {
  if (!location?.coordinates) return null;
  const [lng, lat] = location.coordinates;
  if (!Number.isFinite(lng) || !Number.isFinite(lat)) return null;
  return esriTileUrl(tileContaining(fromCoordinates([lng, lat]), zoom));
}

interface CourseTextureProps {
  /** The course's point. Null/absent → flat green, which is a valid state, not an error. */
  location?: GeoJSONPoint | null;
  children?: ReactNode;
  /** `header` runs the veil left-to-right; `panel` runs it corner-to-corner. */
  veil?: 'header' | 'panel' | 'none';
  className?: string;
  style?: CSSProperties;
}

export function CourseTexture({
  location,
  children,
  veil = 'header',
  className,
  style,
}: CourseTextureProps) {
  const [failed, setFailed] = useState(false);
  const url = failed ? null : tileUrlFor(location, TEXTURE_ZOOM);

  return (
    <div className={cx(styles.root, !url && styles.fallback, className)} style={style}>
      {url && (
        <img
          src={url}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
          className={styles.image}
          onError={() => setFailed(true)}
        />
      )}
      <div
        className={cx(
          styles.veil,
          veil === 'header' && styles.veilHeader,
          veil === 'panel' && styles.veilPanel,
          veil === 'none' && styles.veilNone,
        )}
      />
      <div className={styles.content}>{children}</div>
    </div>
  );
}

/** The 52px (or 44px) rounded thumbnail used in course and home lists. */
export function CourseThumb({
  location,
  size = 52,
  className,
}: {
  location?: GeoJSONPoint | null;
  size?: number;
  className?: string;
}) {
  const [failed, setFailed] = useState(false);
  const url = failed ? null : tileUrlFor(location, THUMB_ZOOM);

  return (
    <div
      className={cx(styles.root, styles.thumb, !url && styles.fallback, className)}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {url && (
        <img
          src={url}
          alt=""
          loading="lazy"
          decoding="async"
          className={styles.image}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
